import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { AppDispatch } from '..';
import { SortObject } from '@bludata/hook-usetableloader';
import { Statist_data_table_interface } from '@bludata/common-interfaces';
import { Statist_ext_table_interface } from '@bludata/common-interfaces';

export interface StatisticheTreeRootProps {
  id: string;
  name: string;
  children?: {
    id: string;
    name: string;
    children?: {
      id: string;
      name: string;
    }[];
  }[];
}

export interface StatisticheSliceState {
  statisticheTable: Statist_ext_table_interface[];
  statisticheLoading: boolean;
  statisticheError: boolean;
  statisticheTree: StatisticheTreeRootProps[];
  loadingHistory: boolean;
  history: Statist_data_table_interface[];
  historyLoaded: {
    [key: string]: boolean;
  };
}

const initialState: StatisticheSliceState = {
  statisticheTable: [],
  statisticheLoading: false,
  statisticheError: false,
  statisticheTree: [],
  loadingHistory: false,
  history: [],
  historyLoaded: {},
};

const StatisticheSlice = createSlice({
  name: 'statistiche',
  initialState,
  reducers: {
    setStatisticheTable(
      state: StatisticheSliceState,
      action: PayloadAction<Statist_ext_table_interface[]>
    ) {
      state.statisticheTable = action.payload;

      const treeRoot: StatisticheTreeRootProps[] = [];

      action.payload.forEach((item, index) => {
        const livello = item.livello;
        const levelLeaf = treeRoot.find((leaf) => leaf.name === livello);

        if (livello.indexOf('Bludata') >= 0) return;

        if (!levelLeaf) {
          treeRoot.push({
            id: `${livello}`,
            name: livello,
            children: [
              {
                id: `${item.id}`,
                name: item.nome,
              },
            ],
          });
        } else {
          levelLeaf.children?.push({
            id: `${item.id}`,
            name: item.nome,
          });
        }
      });

      state.statisticheTree = treeRoot;
    },

    setStatisticheLoading(
      state: StatisticheSliceState,
      action: PayloadAction<boolean>
    ) {
      state.statisticheLoading = action.payload;
    },

    setLoadingHistory(
      state: StatisticheSliceState,
      action: PayloadAction<boolean>
    ) {
      state.loadingHistory = action.payload;
    },

    setHistory(
      state: StatisticheSliceState,
      action: PayloadAction<Statist_data_table_interface[]>
    ) {
      state.history = [...state.history, ...action.payload];
    },

    setLoadingError(
      state: StatisticheSliceState,
      action: PayloadAction<boolean>
    ) {
      state.statisticheError = action.payload;
    },

    setHistoryLoaded(
      state: StatisticheSliceState,
      action: PayloadAction<string>
    ) {
      let obj = {
        ...state.historyLoaded,
      };
      obj[action.payload] = true;
      state.historyLoaded = obj;
    },
  },
});

export const fetchStatistiche = () => {
  return (dispatch: AppDispatch) => {
    dispatch(setStatisticheLoading(true));
    dispatch(setLoadingError(false));

    const sorters: SortObject[] = [
      {
        property: 'livello',
        direction: 'ASC',
      },
      {
        property: 'nome',
        direction: 'ASC',
      },
    ];

    axios
      .get('/focusapi/tabella/statist_ext', {
        params: {
          _dc: new Date().getTime(),
          sort: JSON.stringify(sorters),
        },
      })
      .then((response) => {
        const statisticheList: Statist_ext_table_interface[] =
          response.data.data;
        const success = response.data.success;
        if (success) {
          dispatch(setStatisticheTable(statisticheList));
        } else {
          dispatch(setLoadingError(true));
        }

        dispatch(setStatisticheLoading(false));
      })
      .catch(() => {
        dispatch(setStatisticheLoading(false));
        dispatch(setLoadingError(true));
      });
  };
};

export const loadHistoryForSelectedRecord = (
  statisticheTable: Statist_ext_table_interface[],
  selected: string,
  historyLoaded: {
    [key: string]: boolean;
  }
) => {
  return (dispatch: AppDispatch) => {
    const recordSelected = statisticheTable.find(
      (item) => item.id + '' === selected
    );

    if (
      recordSelected &&
      !historyLoaded.hasOwnProperty(recordSelected.codiceFiliale)
    ) {
      dispatch(setLoadingHistory(true));

      axios
        .get('/focusapi/tabella/statisticadatalista', {
          params: {
            _dc: new Date().getTime(),
            sort: JSON.stringify([
              {
                property: 'data',
                direction: 'DESC',
              },
              {
                property: 'ora',
                direction: 'DESC',
              },
            ]),

            filter: JSON.stringify([
              {
                property: 'codiceRicerca',
                operator: '=',
                value: recordSelected.codiceFiliale,
              },
            ]),
          },
        })
        .then((response) => {
          if (response.data.success) {
            dispatch(setHistoryLoaded(recordSelected.codiceFiliale));
            dispatch(setHistory(response.data.data));
          }

          dispatch(setLoadingHistory(false));
        })
        .catch(() => {
          dispatch(setLoadingHistory(false));
        });
    }
  };
};

export const {
  setStatisticheTable,
  setStatisticheLoading,
  setLoadingError,
  setHistory,
  setHistoryLoaded,
  setLoadingHistory,
} = StatisticheSlice.actions;

export default StatisticheSlice.reducer;
